'use client'

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

type MarqueeProps = {
  duration?: number
  pauseOnHover?: boolean
  mask?: boolean
  className?: string
  children: ReactNode
}

export default function Marquee({
  duration = 30,
  pauseOnHover = false,
  mask = true,
  className,
  children,
}: MarqueeProps) {
  const outerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [repetitions, setRepetitions] = useState(2)
  const [measured, setMeasured] = useState(false)
  const prevWidthRef = useRef<number>(0)

  const calculateRepetitions = () => {
    if (!outerRef.current || !contentRef.current || !trackRef.current) return

    const width = contentRef.current.offsetWidth
    const visibleWidth = outerRef.current.offsetWidth
    const safeWidth = width || visibleWidth || 1

    trackRef.current.style.setProperty('--single-set-size', `${safeWidth}px`)

    const neededRepetitions = Math.ceil(visibleWidth / safeWidth) + 1
    setRepetitions(Math.max(2, neededRepetitions))
    setMeasured(true)
  }

  useEffect(() => {
    calculateRepetitions()

    let resizeTimeout: ReturnType<typeof setTimeout> | undefined

    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        calculateRepetitions()
      }, 100)
    }

    // Window resize
    window.addEventListener('resize', handleResize, { passive: true })

    // Observe intrinsic content size changes (e.g., fonts, images)
    let observerTimeout: NodeJS.Timeout
    const observer = new ResizeObserver(() => {
      if (!contentRef.current) return
      const newWidth = contentRef.current.offsetWidth
      if (newWidth !== prevWidthRef.current) {
        prevWidthRef.current = newWidth
        clearTimeout(observerTimeout)
        observerTimeout = setTimeout(() => {
          calculateRepetitions()
        }, 100)
      }
    })

    const currentContent = contentRef.current
    if (currentContent) observer.observe(currentContent)

    return () => {
      clearTimeout(resizeTimeout)
      clearTimeout(observerTimeout)
      window.removeEventListener('resize', handleResize)
      observer.disconnect()
    }
  }, [children])

  const gradientMask =
    'linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 10%, rgba(0, 0, 0, 1) 90%, rgba(0, 0, 0, 0) 100%)'

  return (
    <div
      ref={outerRef}
      style={{ mask: mask ? gradientMask : undefined }}
      className={cn('relative w-full overflow-hidden', className)}
    >
      <div
        ref={trackRef}
        className={cn(
          'flex animate-marquee transition-[animation-play-state] will-change-transform',
          pauseOnHover && 'hover:pause',
          !measured && 'invisible',
        )}
        style={
          {
            '--duration': `${duration}s`,
            '--offset-start': '0px',
            '--single-set-size': '0px',
            '--offset-end': '0px',
          } as CSSProperties
        }
      >
        <div ref={contentRef} className="flex shrink-0">
          {children}
        </div>
        {Array.from({ length: repetitions - 1 }).map((_, index) => (
          <div key={index} className="flex shrink-0">
            {children}
          </div>
        ))}
      </div>
    </div>
  )
}
